import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  ENTRELINHAS,
  ESTILOS,
  FONTES,
  ORDEM_ESTILOS,
  TAMANHOS,
  diferencaDeFormato,
  estiloPersonalizado,
  fmtNum,
  formatoDoPronto,
  pilhaDaFonte,
  resumoDoFormato,
  type AjustesDosEstilosProntos,
  type Alinhamento,
  type EstiloPersonalizado,
  type FormatoParagrafo,
  type NomeEstilo,
  type RecuoEspecial,
} from '../estilos';
import { Numero, PainelDialogo, enterAplica, rodapeModal } from './paineis';
import { BotaoNu } from './comum';
// Os campos e a amostra também saem fora do editor (a aba Peças do escritório).
import '../editor-forense.css';

/** Estilos do escritório: criar a partir do parágrafo e gerenciar. */

const NOMES_ALINHAMENTO: Record<Alinhamento, string> = { left: 'À esquerda', center: 'Centralizado', right: 'À direita', justify: 'Justificado' };
const NOMES_ESPECIAL: Record<RecuoEspecial, string> = { nenhum: 'Nenhum', primeira: 'Primeira linha', deslocado: 'Deslocado' };
const AMOSTRA = 'MARIA APARECIDA DOS SANTOS, brasileira, servidora pública aposentada, inscrita no CPF sob o nº 123.456.789-00, vem, por seu advogado, propor a presente ação.';

/** A amostra de um formato, em miniatura fiel (cm viram px na escala da prévia). */
export function AmostraDoFormato({ f, texto = AMOSTRA, escala = 22 }: { f: FormatoParagrafo; texto?: string; escala?: number }) {
  const recuo = f.especial === 'primeira' ? { textIndent: f.medida * escala } : f.especial === 'deslocado' ? { textIndent: -f.medida * escala, paddingLeft: f.medida * escala } : {};
  return (
    <div className="cm-ef-amostra-folha">
      <p style={{
        margin: `${f.espacoAntes * 0.6}px ${f.recuoDireito * escala}px ${f.espacoDepois * 0.6}px ${f.recuoEsquerdo * escala}px`,
        fontFamily: pilhaDaFonte(f.fonte), fontSize: f.tamanho, lineHeight: f.entrelinha, textAlign: f.alinhamento,
        fontWeight: f.negrito ? 700 : 400, fontStyle: f.italico ? 'italic' : 'normal', ...recuo,
      }}>{texto}</p>
    </div>
  );
}

function linhasDoFormato(f: FormatoParagrafo, base: FormatoParagrafo): { rotulo: string; valor: string; difere: boolean }[] {
  const recuo = f.especial === 'nenhum' ? (f.recuoEsquerdo ? `${fmtNum(f.recuoEsquerdo)} cm à esquerda` : 'Sem recuo')
    : `${NOMES_ESPECIAL[f.especial]} ${fmtNum(f.medida)} cm${f.recuoEsquerdo ? ` · ${fmtNum(f.recuoEsquerdo)} cm à esquerda` : ''}`;
  return [
    { rotulo: 'Fonte', valor: `${f.fonte} ${fmtNum(f.tamanho)}${f.negrito ? ' · negrito' : ''}${f.italico ? ' · itálico' : ''}`, difere: f.fonte !== base.fonte || f.tamanho !== base.tamanho || f.negrito !== base.negrito || f.italico !== base.italico },
    { rotulo: 'Alinhamento', valor: NOMES_ALINHAMENTO[f.alinhamento], difere: f.alinhamento !== base.alinhamento },
    { rotulo: 'Recuos', valor: recuo, difere: f.especial !== base.especial || f.medida !== base.medida || f.recuoEsquerdo !== base.recuoEsquerdo || f.recuoDireito !== base.recuoDireito },
    { rotulo: 'Espaçamento', valor: `${fmtNum(f.espacoAntes)} pt antes · ${fmtNum(f.espacoDepois)} pt depois`, difere: f.espacoAntes !== base.espacoAntes || f.espacoDepois !== base.espacoDepois },
    { rotulo: 'Entrelinha', valor: ENTRELINHAS.find((e) => e.valor === f.entrelinha)?.rotulo ?? fmtNum(f.entrelinha), difere: f.entrelinha !== base.entrelinha },
  ];
}

/* ===================== Criar estilo ===================== */

export function PainelCriarEstilo({ aberto, formato, estiloAtual, podeEscritorio, rotuloEscritorio, aoFechar, aoCriar }: {
  aberto: boolean;
  /** O formato do parágrafo do cursor, já com os ajustes. */
  formato: FormatoParagrafo;
  estiloAtual: string;
  podeEscritorio: boolean;
  rotuloEscritorio: string;
  aoFechar: () => void;
  aoCriar: (e: Omit<EstiloPersonalizado, 'id' | 'criadoEm' | 'criadoPor'>) => void;
}) {
  const baseInicial: NomeEstilo = (estiloAtual in ESTILOS ? estiloAtual : estiloPersonalizado(estiloAtual)?.baseadoEm ?? 'corpo') as NomeEstilo;
  const [nome, setNome] = useState('');
  const [baseadoEm, setBaseadoEm] = useState<NomeEstilo>(baseInicial);
  const [alcance, setAlcance] = useState<'escritorio' | 'pessoal'>(podeEscritorio ? 'escritorio' : 'pessoal');
  const [erro, setErro] = useState('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (aberto) { setNome(''); setBaseadoEm(baseInicial); setErro(''); } }, [aberto]);
  const base = formatoDoPronto(baseadoEm);
  const criar = () => {
    if (!nome.trim()) { setErro('Dê um nome ao estilo.'); return; }
    aoCriar({ nome: nome.trim(), baseadoEm, ajustes: diferencaDeFormato(base, formato), alcance });
  };
  return (
    <PainelDialogo aberto={aberto} aoFechar={aoFechar} titulo="Novo estilo"
      descricao="A partir do parágrafo do cursor. Ele entra no menu de estilos com a amostra ao lado."
      rodape={rodapeModal(criar, aoFechar, 'Criar estilo')}>
      <div className="cm-ef-painel" onKeyDown={enterAplica(criar)}>
        <div className="cm-ef-painel-campos">
          <section className="cm-ef-secao">
            <label className="cm-ef-rot">Nome do estilo
              <input className="cm-ef-campo" autoFocus value={nome} onChange={(e) => { setNome(e.target.value); setErro(''); }} placeholder="Qualificação das partes" />
            </label>
            {erro && <p className="cm-ef-erro-campo" role="alert">{erro}</p>}
            <label className="cm-ef-rot">Baseado em
              <select className="cm-ef-campo" value={baseadoEm} onChange={(e) => setBaseadoEm(e.target.value as NomeEstilo)}>
                {ORDEM_ESTILOS.map((k) => <option key={k} value={k}>{ESTILOS[k].nome}</option>)}
              </select>
            </label>
            <p className="cm-ef-mini-leg">O que você não mudou continua seguindo o “{ESTILOS[baseadoEm].nome}”.</p>
          </section>
          <section className="cm-ef-secao">
            <h3>O que o estilo guarda</h3>
            <dl className="cm-ef-resumo-estilo">
              {linhasDoFormato(formato, base).map((l) => (
                <div key={l.rotulo} className={l.difere ? 'is-difere' : ''}><dt>{l.rotulo}</dt><dd>{l.valor}</dd></div>
              ))}
            </dl>
            <p className="cm-ef-mini-leg">Em destaque, o que difere do “{ESTILOS[baseadoEm].nome}”.</p>
          </section>
          <fieldset className="cm-ef-secao cm-ef-alcance">
            <legend>Para quem vale</legend>
            <label className={`cm-ef-opcao${alcance === 'escritorio' ? ' is-ativo' : ''}${podeEscritorio ? '' : ' is-desligada'}`}>
              <input type="radio" name="alcance" checked={alcance === 'escritorio'} disabled={!podeEscritorio} onChange={() => setAlcance('escritorio')} /> {rotuloEscritorio}
            </label>
            <label className={`cm-ef-opcao${alcance === 'pessoal' ? ' is-ativo' : ''}`}>
              <input type="radio" name="alcance" checked={alcance === 'pessoal'} onChange={() => setAlcance('pessoal')} /> Só para mim
            </label>
          </fieldset>
        </div>
        <aside className="cm-ef-diagrama" aria-label="Como fica">
          <h3>Como fica</h3>
          <AmostraDoFormato f={formato} />
        </aside>
      </div>
    </PainelDialogo>
  );
}

/* ===================== Gerenciar estilos ===================== */

type Selecao = { tipo: 'pronto'; nome: NomeEstilo } | { tipo: 'personalizado'; id: string };

export function PainelGerenciarEstilos({ aberto, personalizados, ajustesProntos, ajustesPessoais = {}, escritorioPrevalece = true, rotuloEscritorio, podeEscritorio, aoFechar, aoSalvarPersonalizado, aoExcluirPersonalizado, aoAjustarPronto, aoRemoverPessoal, aoDuplicar }: {
  aberto: boolean;
  personalizados: EstiloPersonalizado[];
  /** Os ajustes dos prontos de todo o escritório: é o que este painel muda. */
  ajustesProntos: AjustesDosEstilosProntos;
  /** Os "só para mim" de quem escreve (vêm da Formatação do texto): aqui só se removem. */
  ajustesPessoais?: AjustesDosEstilosProntos;
  /** Onde os dois ajustam, vale o do escritório (obrigatório) ou o seu (sugerido). */
  escritorioPrevalece?: boolean;
  aoRemoverPessoal?: (nome: NomeEstilo) => void;
  rotuloEscritorio: string;
  /** Mexer nos estilos prontos e nos do escritório vale para todos: o app diz se esta pessoa pode. */
  podeEscritorio: boolean;
  aoFechar: () => void;
  aoSalvarPersonalizado: (e: EstiloPersonalizado) => void;
  aoExcluirPersonalizado: (id: string) => void;
  aoAjustarPronto: (nome: NomeEstilo, ajustes: Partial<FormatoParagrafo> | null) => void;
  aoDuplicar: (e: Omit<EstiloPersonalizado, 'id' | 'criadoEm' | 'criadoPor'>) => void;
}) {
  const [sel, setSel] = useState<Selecao>({ tipo: 'pronto', nome: 'corpo' });
  const pers = sel.tipo === 'personalizado' ? personalizados.find((p) => p.id === sel.id) ?? null : null;
  // O pronto parte do padrão com o ajuste do ESCRITÓRIO (o que se muda aqui), não do
  // que vale na peça aberta — o "só para mim" dela não vira de todos ao salvar.
  const doEscritorio = (n: NomeEstilo): FormatoParagrafo => ({ ...formatoDoPronto(n, false), ...(ajustesProntos[n] ?? {}) });
  const formatoDaSelecao = (): FormatoParagrafo => (pers ? { ...formatoDoPronto(pers.baseadoEm), ...pers.ajustes } : doEscritorio(sel.tipo === 'pronto' ? sel.nome : 'corpo'));
  const [f, setF] = useState<FormatoParagrafo>(formatoDaSelecao);
  const [nome, setNome] = useState('');
  // Recarrega o formulário ao trocar de estilo ou ao abrir.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setF(formatoDaSelecao()); setNome(pers?.nome ?? ''); }, [aberto, sel.tipo, sel.tipo === 'pronto' ? sel.nome : sel.id]);
  const m = (p: Partial<FormatoParagrafo>) => setF((x) => ({ ...x, ...p }));
  const editavel = pers ? pers.alcance === 'pessoal' || podeEscritorio : podeEscritorio;
  const prontoAjustado = sel.tipo === 'pronto' && !!ajustesProntos[sel.nome] && Object.keys(ajustesProntos[sel.nome] ?? {}).length > 0;
  const meuAjuste = sel.tipo === 'pronto' && aoRemoverPessoal && Object.keys(ajustesPessoais[sel.nome] ?? {}).length > 0 ? sel.nome : null;

  const salvar = () => {
    if (!editavel) return;
    if (pers) aoSalvarPersonalizado({ ...pers, nome: nome.trim() || pers.nome, ajustes: diferencaDeFormato(formatoDoPronto(pers.baseadoEm), f) });
    else if (sel.tipo === 'pronto') {
      const d = diferencaDeFormato(formatoDoPronto(sel.nome, false), f);
      aoAjustarPronto(sel.nome, Object.keys(d).length ? d : null);
    }
  };
  const item = (s: Selecao, rotulo: string, det?: string, marca?: string) => {
    const ativo = s.tipo === sel.tipo && (s.tipo === 'pronto' ? s.nome === (sel as { nome: NomeEstilo }).nome : s.id === (sel as { id: string }).id);
    return (
      <BotaoNu key={s.tipo === 'pronto' ? s.nome : s.id} className={`cm-ef-est-item${ativo ? ' is-ativo' : ''}`} aria-current={ativo || undefined} onClick={() => setSel(s)}>
        <span className="cm-ef-est-nome">{rotulo}{marca && <em className="cm-ef-est-marca">{marca}</em>}</span>
        {det && <span className="cm-ef-est-det">{det}</span>}
      </BotaoNu>
    );
  };

  return (
    <PainelDialogo aberto={aberto} aoFechar={aoFechar} titulo="Estilos"
      descricao="Os prontos e os do escritório. Mudar um estilo muda todos os parágrafos escritos nele."
      rodape={editavel ? rodapeModal(salvar, aoFechar, 'Salvar estilo') : rodapeModal(aoFechar, aoFechar, 'Fechar')}>
      <div className="cm-ef-gerenciar" onKeyDown={enterAplica(salvar)}>
        <nav className="cm-ef-est-lista" aria-label="Estilos">
          <span className="cm-ef-est-grupo">Prontos</span>
          {ORDEM_ESTILOS.map((k) => item({ tipo: 'pronto', nome: k }, ESTILOS[k].nome, undefined, ajustesProntos[k] && Object.keys(ajustesProntos[k] ?? {}).length ? 'ajustado' : undefined))}
          <span className="cm-ef-est-grupo">{rotuloEscritorio}</span>
          {personalizados.length === 0 && <span className="cm-ef-mini-leg">Nenhum ainda. Crie um pelo menu de estilos, a partir de um parágrafo.</span>}
          {personalizados.map((p) => item({ tipo: 'personalizado', id: p.id }, p.nome,
            p.alcance === 'pessoal' ? 'só para mim' : [p.criadoPor && `criado por ${p.criadoPor}`, p.criadoEm && p.criadoEm.slice(0, 10).split('-').reverse().join('/')].filter(Boolean).join(' · ') || undefined))}
        </nav>
        <div className="cm-ef-est-form">
          <div className="cm-ef-est-topo">
            {pers ? (
              <label className="cm-ef-rot cm-ef-est-nomecampo">Nome
                <input className="cm-ef-campo" value={nome} disabled={!editavel} onChange={(e) => setNome(e.target.value)} />
              </label>
            ) : (
              <h3 className="cm-ef-est-titulo">{sel.tipo === 'pronto' ? ESTILOS[sel.nome].nome : ''}</h3>
            )}
            <div className="cm-ef-acoes">
              {pers && <Button type="button" size="sm" variant="outline" onClick={() => aoDuplicar({ nome: `${pers.nome} (cópia)`, baseadoEm: pers.baseadoEm, ajustes: diferencaDeFormato(formatoDoPronto(pers.baseadoEm), f), alcance: 'pessoal' })}>Duplicar</Button>}
              {pers && editavel && <Button type="button" size="sm" variant="outline" className="text-destructive" onClick={() => { aoExcluirPersonalizado(pers.id); setSel({ tipo: 'pronto', nome: 'corpo' }); }}>Excluir</Button>}
              {prontoAjustado && editavel && sel.tipo === 'pronto' && <Button type="button" size="sm" variant="outline" onClick={() => { aoAjustarPronto(sel.nome, null); setF(formatoDoPronto(sel.nome, false)); }}>Restaurar o padrão</Button>}
            </div>
          </div>
          {!editavel && <p className="cm-ef-mini-leg">Este estilo vale para o escritório; quem o administra pode mudá-lo.</p>}
          {meuAjuste && (
            <p className="cm-ef-mini-leg">
              Você tem um “{ESTILOS[meuAjuste].nome}” seu ({resumoDoFormato({ ...formatoDoPronto(meuAjuste, false), ...ajustesPessoais[meuAjuste] })}), com que as suas próximas peças abrem{escritorioPrevalece ? '; onde o escritório ajustou, vale o dele' : ''}.{' '}
              <Button type="button" size="sm" variant="link" className="h-auto p-0" onClick={() => aoRemoverPessoal!(meuAjuste)}>Remover o meu</Button>
            </p>
          )}
          <CamposDoFormato f={f} aoMudar={m} desabilitado={!editavel} />
          <h3 className="cm-ef-est-titulo">Amostra</h3>
          <AmostraDoFormato f={f} />
        </div>
      </div>
    </PainelDialogo>
  );
}

/**
 * Os campos do formato de um parágrafo (fonte, tamanho, recuos, espaços,
 * entrelinha): o "Gerenciar estilos" do editor e a aba "Peças" do escritório (o
 * Corpo da peça dele) usam os mesmos.
 */
export function CamposDoFormato({ f, aoMudar: m, desabilitado = false }: {
  f: FormatoParagrafo;
  aoMudar: (p: Partial<FormatoParagrafo>) => void;
  desabilitado?: boolean;
}) {
  return (
    <fieldset className="cm-ef-est-campos" disabled={desabilitado}>
      <div className="cm-ef-linhas">
        <label className="cm-ef-rot">Fonte
          <select className="cm-ef-campo" value={f.fonte} onChange={(e) => m({ fonte: e.target.value })}>{FONTES.map((x) => <option key={x.nome}>{x.nome}</option>)}</select>
        </label>
        <label className="cm-ef-rot">Tamanho
          <select className="cm-ef-campo" value={f.tamanho} onChange={(e) => m({ tamanho: Number(e.target.value) })}>{TAMANHOS.map((t) => <option key={t} value={t}>{t}</option>)}</select>
        </label>
        <label className="cm-ef-rot">Alinhamento
          <select className="cm-ef-campo" value={f.alinhamento} onChange={(e) => m({ alinhamento: e.target.value as Alinhamento })}>
            {(Object.keys(NOMES_ALINHAMENTO) as Alinhamento[]).map((a) => <option key={a} value={a}>{NOMES_ALINHAMENTO[a]}</option>)}
          </select>
        </label>
        <div className="cm-ef-rot">Destaque
          <span className="cm-ef-linha-bt">
            <label className="cm-ef-check"><input type="checkbox" checked={f.negrito} onChange={(e) => m({ negrito: e.target.checked })} /> Negrito</label>
            <label className="cm-ef-check"><input type="checkbox" checked={f.italico} onChange={(e) => m({ italico: e.target.checked })} /> Itálico</label>
          </span>
        </div>
        <Numero rotulo="Recuo à esquerda" valor={f.recuoEsquerdo} unidade="cm" passo={0.25} min={0} max={10} aoMudar={(v) => m({ recuoEsquerdo: v })} />
        <Numero rotulo="Recuo à direita" valor={f.recuoDireito} unidade="cm" passo={0.25} min={0} max={8} aoMudar={(v) => m({ recuoDireito: v })} />
        <label className="cm-ef-rot">Especial
          <select className="cm-ef-campo" value={f.especial} onChange={(e) => m({ especial: e.target.value as RecuoEspecial })}>
            {(Object.keys(NOMES_ESPECIAL) as RecuoEspecial[]).map((x) => <option key={x} value={x}>{NOMES_ESPECIAL[x]}</option>)}
          </select>
        </label>
        <Numero rotulo="Medida" valor={f.medida} unidade="cm" passo={0.25} min={0} max={8} aoMudar={(v) => m({ medida: v })} />
        <Numero rotulo="Espaço antes" valor={f.espacoAntes} unidade="pt" passo={6} min={0} max={72} aoMudar={(v) => m({ espacoAntes: v })} />
        <Numero rotulo="Espaço depois" valor={f.espacoDepois} unidade="pt" passo={6} min={0} max={72} aoMudar={(v) => m({ espacoDepois: v })} />
        <label className="cm-ef-rot">Entrelinha
          <select className="cm-ef-campo" value={String(f.entrelinha)} onChange={(e) => m({ entrelinha: Number(e.target.value) })}>
            {ENTRELINHAS.map((x) => <option key={x.valor} value={String(x.valor)}>{x.rotulo}</option>)}
          </select>
        </label>
      </div>
    </fieldset>
  );
}
